import { FormBuilder } from '@/components/formBuilder';
import { Flex, Icon } from '@chakra-ui/react';
import { KText } from '@/ui-library/components';
import {
  SelectOptionWithAvatar,
  SelectWithDescriptionOption,
} from '@/components/formBuilder/SelectComponents';
import { useSelector } from 'react-redux';
import { selectUsers } from '@/store/users/selectors';
import { ViewFile } from '@/components/ats';
import { useField } from 'formik';
import { useGetTemplatesVariablesQuery } from '@/store/query/email-templates-settings';
import { useState } from 'react';
import { Check, Copy03, Link01 } from '@kula-ai/icons-core';
import * as Yup from 'yup';
import { useShowToast } from '@/hooks';
import { getErrorMessageForAssessmentFiles } from '@/utils/getAssessmentFilesError';
import { DEFAULT_ATTACHMENT_FILES } from '@/constants/attachment-files';
import { ScorecardThresholdField } from '@/components/ats/scorecard-threshold-selector';

const getDetailsInitialValue = (activity) => {
  const {
    name,
    participants,
    text,
    attachments,
    reviewer_instructions,
    reviewer_instructions_attachments = [],
    scorecard_completion_threshold,
  } = activity || {};

  return {
    label: name,
    reviewer_ids: participants?.map((item) => item?.id),
    assessment: text,
    assessment_upload: attachments,
    reviewer_instructions: reviewer_instructions,
    reviewer_attachments: reviewer_instructions_attachments,
    scorecard_completion_threshold: scorecard_completion_threshold ?? null,
  };
};

export const Details = ({
  formRef = null,
  activity = {} as any,
  isAdhoc = false,
}) => {
  const labelStyles = { fontSize: '14px', fontWeight: 'semiBold' };
  const containerStyles = { paddingLeft: '24px', paddingRight: '24px' };
  const users = useSelector(selectUsers);
  const usersOptions = users?.map((user) => ({
    ...user,
    label: user?.name,
    value: user?.id,
  }));
  const { data: { variables = [] } = {} } = useGetTemplatesVariablesQuery({
    params: { view: ['job', 'candidate', 'org'] },
  });

  const initialValues = getDetailsInitialValue(activity);
  const isCreateFlow = activity?.status === 'pending';
  const isSubmitted = activity?.status === 'submitted';
  const toast = useShowToast();

  const formConfig = [
    // ! assessment commenting for V1
    // {
    //   type: 'seperator',
    //   seperatorConfig: {
    //     containerStyles: {
    //       padding: '16px 24px',
    //       backgroundColor: 'ats-tertiary',
    //     },
    //     textStyles: {
    //       color: 'ats-primary',
    //       size: 'sm-medium',
    //       textTransform: 'uppercase',
    //     },
    //     title: 'Details',
    //   },
    // },
    !isCreateFlow &&
      !isSubmitted &&
      !isAdhoc && {
        name: 'assessment_link',
        type: 'custom',
        Field: () => {
          const [copied, setCopied] = useState(false);
          const assessmentLink = activity?.public_link || '';

          return (
            <Flex
              flexDir="column"
              mt="12px"
              mx="24px"
              bg="ats-bg-primary"
              borderRadius="sm"
              p="14px"
              gap="12px"
            >
              <KText size="sm-semi-bold">Assessment link</KText>
              <KText size="xs-medium" color="secondary-700">
                You can access this assessment using the link below. We&apos;ll
                also include the link in the email sent to the candidate.
              </KText>
              <Flex
                bg="ats-secondary"
                p="10px"
                borderRadius="sm"
                gap="12px"
                justifyContent="space-between"
                alignItems="center"
              >
                <Flex gap="12px" alignItems="center">
                  <Icon
                    // @ts-expect-error - Link01 is an IconType
                    as={Link01}
                  />
                  <KText size="sm-medium">{assessmentLink}</KText>
                </Flex>
                <Icon
                  onClick={() => {
                    navigator.clipboard.writeText(assessmentLink);
                    setCopied(true);
                    setTimeout(() => {
                      setCopied(false);
                    }, 1000);
                  }}
                  cursor="pointer"
                  // @ts-expect-error - Check/Copy03 are IconType
                  as={copied ? Check : Copy03}
                />
              </Flex>
            </Flex>
          );
        },
      },
    {
      name: 'label',
      label: 'Name',
      type: 'input',
      isRequired: true,
      placeholder: 'Add assessment name here',
      containerStyles: { width: '100%', ...containerStyles },
      maxLength: 250,
      labelStyles: labelStyles,
      errorMessage: 'Name is required',
    },
    {
      name: 'reviewer_ids',
      label: 'Add reviewer(s)',
      type: 'select',
      isRequired: true,
      isMulti: true,
      width: '100%',
      containerStyles: containerStyles,
      labelStyles: labelStyles,
      customFormatLabel: SelectOptionWithAvatar,
      components: {
        Option: SelectWithDescriptionOption,
      },
      showAvatar: true,
      descKey: 'email',
      avatarKey: 'avatar',
      isNearLabel: true,
      options: usersOptions,
      valueKey: 'id',
      placeholder: 'Select reviewer',
      errorMessage: 'Reviewer(s) is required',
    },
    {
      name: 'scorecard_completion_threshold',
      type: 'custom',
      Field: ScorecardThresholdField,
      props: {
        containerStyles: { ml: '24px' },
      },
    },
    isSubmitted && {
      name: 'assessment_submitted_note',
      type: 'custom',
      Field: () => {
        return (
          <Flex
            flexDir="column"
            mt="12px"
            mx="24px"
            bg="ats-bg-primary"
            borderRadius="sm"
            p="14px"
            mb="200px"
          >
            <KText fontSize="sm" ml="4px">
              <b>Note:</b> The candidate has already submitted the assessment.
              While you can&apos;t edit the original assessment anymore, you can
              still edit the reviewers and the scorecard.
            </KText>
          </Flex>
        );
      },
    },
    !isSubmitted && {
      name: 'assessment',
      label: 'Assessment question',
      type: 'editor',
      isRequired: true,
      containerStyles: containerStyles,
      labelStyles: labelStyles,
      enableCustomVariables: true,
      hasCodeBlock: true,
      resize: true,
      customVariableList: variables,
      validation: Yup.string()
        .required('Assessment question is required')
        .max(
          10000,
          'You can add up to 10,000 characters. For longer assessments, consider adding them as attachments.'
        )
        .test(
          'check-text-inside-html-check',
          'Assessment question is required',
          function (value) {
            const regex =
              /<([a-zA-Z][\w-]*)(\s+[^>]*)?\/?>\s*([^<]*)\s*(<\/\1>)?/g;
            const result = [];
            let match;
            while ((match = regex.exec(value)) !== null) {
              const tagName = match[1];
              const innerText = match[3]?.trim() || ''; // Inner text, if present
              const isSelfClosing =
                match[3] === '/' || tagName.toLowerCase() === 'br'; // Detect self-closing tags or <br>
              if (!isSelfClosing) result.push(innerText); // Push only for non-self-closing tags
            }
            return Boolean(result?.length);
          }
        ),
    },
    !isSubmitted && {
      name: 'assessment_upload_preview',
      label: 'Uploaded files',
      type: 'custom',
      containerStyles: containerStyles,
      isConditional: true,
      conditionConfig: {
        field: 'assessment_upload',
        value: 0,
        type: 'greater',
      },
      Field: () => {
        const [, meta, helpers] = useField('assessment_upload');
        const { value = [] } = meta;
        const { setValue } = helpers;

        const handleDelete = (file) => {
          if (file instanceof File) setValue(value.filter((fi) => fi !== file));
          else {
            setValue(value.filter((fi) => fi?.id !== file?.id));
          }
        };

        if (!value?.length) return null;
        return (
          <Flex flexDir="column" gap="12px" px="24px">
            {value?.map((item, index) => {
              return (
                <ViewFile
                  key={item?.id || item?.name || index}
                  file={item}
                  showDelete
                  handleDelete={() => handleDelete(item)}
                  showDownload={item instanceof File ? false : true}
                  showPreview={item instanceof File ? false : true}
                />
              );
            })}
          </Flex>
        );
      },
    },
    !isSubmitted && {
      name: 'assessment_upload',
      type: 'upload',
      containerStyles: containerStyles,
      maxFiles: 10,
      description:
        'Supported: Documents (PDF, Word, Excel and more), Images (JPG, PNG and more), Text & Archive files',
      showFiles: false,
      appendFiles: true,
      maxSize: 10 * 1024 * 1024, // 10 MB
      acceptedFiles: DEFAULT_ATTACHMENT_FILES.join(','),
      handleUnAcceptedFiles: (files = []) => {
        const errorCodes = files?.map((item) => item?.errors?.[0]?.code);
        const errorMessages = getErrorMessageForAssessmentFiles({ errorCodes });
        toast({ status: 'error', title: errorMessages?.join(',') });
      },
    },
    // ! assessment commenting for V1
    // {
    //   type: 'seperator',
    //   seperatorConfig: {
    //     containerStyles: {
    //       padding: '16px 24px',
    //       backgroundColor: 'ats-tertiary',
    //     },
    //     textStyles: {
    //       color: 'ats-primary',
    //       size: 'sm-medium',
    //       textTransform: 'uppercase',
    //     },
    //     title: 'Instructions',
    //   },
    // },
    // {
    //   name: 'reviewer_instructions',
    //   label: 'Reviewer Instructions',
    //   type: 'editor',
    //   isRequired: false,
    //   containerStyles: containerStyles,
    //   labelStyles: labelStyles,
    //   pluginsConfig: ['link', 'image', 'attachment'],
    //   attachmentKey: 'reviewer_attachments',
    //   attachmentFiles: acceptedFiles.join(','),
    //   customVariableList: variables,
    //   enableCustomVariables: true,
    // },
  ].filter(Boolean);

  return (
    <Flex maxH="100%" flexDir="column">
      <FormBuilder
        config={formConfig}
        formRef={formRef}
        initialValues={initialValues}
        showCustomError={false}
      />
    </Flex>
  );
};
